import { useQuery } from '@tanstack/react-query';
import { Loader2, LogIn, AlertTriangle, Clock } from 'lucide-react';
import { doorApi, type DoorAccessLogEntry } from '@/api/door';

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин. назад`;
  if (diffHour < 24) return `${diffHour} ч. назад`;
  if (diffDay === 1) return 'вчера';
  if (diffDay < 7) return `${diffDay} дн. назад`;
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

const EVENT_CONFIG: Record<string, { label: string; icon: typeof LogIn; className: string }> = {
  token_generated: { label: 'Токен создан', icon: Clock, className: 'text-blue-500' },
  door_opened: { label: 'Дверь открыта', icon: LogIn, className: 'text-green-500' },
  door_failed: { label: 'Ошибка открытия', icon: AlertTriangle, className: 'text-red-500' },
};

function LogEntry({ entry }: { entry: DoorAccessLogEntry }) {
  const config = EVENT_CONFIG[entry.event] || EVENT_CONFIG.token_generated;
  const Icon = config.icon;
  const date = new Date(entry.createdAt);

  return (
    <div className="flex items-center gap-3 py-3 border-b border-border last:border-0">
      <div className={`shrink-0 ${config.className}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{config.label}</p>
        <p className="text-xs text-muted-foreground">
          {formatTimeAgo(date)}
          {entry.ipAddress && ` • ${entry.ipAddress}`}
        </p>
      </div>
      <div className="text-xs text-muted-foreground shrink-0">
        {date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
}

export function MyAccessLog() {
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['door', 'my-log'],
    queryFn: () => doorApi.getMyLog(50),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        История входов пуста
      </p>
    );
  }

  return (
    <div className="divide-y divide-border">
      {entries.map((entry) => (
        <LogEntry key={entry.id} entry={entry} />
      ))}
    </div>
  );
}
